import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ChevronLeft,
  BatteryCharging,
  BatteryFull,
  Plug,
  Unplug,
  Droplets,
  Waves,
  Gauge,
  KeyRound,
  Clock,
  Thermometer,
  ShieldCheck,
  AlertTriangle,
  Cog,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useFleetStore } from '@/stores/fleet';
import { readTelemetry, isBlocked } from '@/lib/machineStatus';
import { cn, formatRelativeTime } from '@/lib/utils';
import type { Position } from '@/api/types';

/** Lê o primeiro atributo presente entre várias chaves possíveis. */
function pick(attrs: Record<string, unknown> | undefined, keys: string[]): unknown {
  if (!attrs) return undefined;
  for (const k of keys) {
    if (attrs[k] !== undefined && attrs[k] !== null && attrs[k] !== '') return attrs[k];
  }
  return undefined;
}

function asBool(v: unknown): boolean | undefined {
  if (v === undefined || v === null || v === '') return undefined;
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  if (typeof v === 'string') {
    const s = v.toLowerCase();
    if (['true', '1', 'on', 'sim', 'yes', 'alarm'].includes(s)) return true;
    if (['false', '0', 'off', 'nao', 'não', 'no'].includes(s)) return false;
  }
  return undefined;
}

function asNumber(v: unknown): number | undefined {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

type RowTone = 'ok' | 'warn' | 'danger' | 'muted';

interface HealthRow {
  icon: typeof Droplets;
  label: string;
  value: string;
  tone: RowTone;
}

const TONE: Record<RowTone, string> = {
  ok: 'text-status-running',
  warn: 'text-status-ready',
  danger: 'text-status-blocked',
  muted: 'text-muted-foreground',
};

export function HealthPage() {
  const { id } = useParams<{ id: string }>();
  const deviceId = Number(id);
  const device = useFleetStore((s) => s.devices.get(deviceId));
  const position = useFleetStore((s) => s.positions.get(deviceId));

  const data = useMemo(() => buildHealth(device ? position : undefined, device ? isBlocked(device, position) : false), [device, position]);

  if (!device) {
    return (
      <div className="space-y-4 p-4 sm:p-6">
        <BackLink />
        <p className="text-muted-foreground">Embarcação não encontrada.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5 p-4 sm:p-6">
      <BackLink title={device.name} />

      {/* Diagnóstico geral */}
      <div className="flex justify-center">
        <span
          className={cn(
            'inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold',
            data.overall === 'ok' && 'bg-status-running/15 text-status-running',
            data.overall === 'warn' && 'bg-status-ready/15 text-status-ready',
            data.overall === 'danger' && 'bg-status-blocked/15 text-status-blocked',
          )}
        >
          {data.overall === 'danger' ? (
            <AlertTriangle className="h-4 w-4" />
          ) : data.overall === 'warn' ? (
            <AlertTriangle className="h-4 w-4" />
          ) : (
            <ShieldCheck className="h-4 w-4" />
          )}
          {data.overall === 'danger'
            ? 'Atenção: alerta crítico'
            : data.overall === 'warn'
              ? 'Diagnóstico: requer atenção'
              : 'Diagnóstico geral: OK'}
        </span>
      </div>

      {position?.fixTime && (
        <p className="-mt-3 text-center text-xs text-muted-foreground">
          Última leitura {formatRelativeTime(position.fixTime)}
        </p>
      )}

      <HealthSection title="Energia" rows={data.energia} />
      <HealthSection title="Casco / Porão" rows={data.casco} />
      <HealthSection title="Motor" rows={data.motor} />

      {!position && (
        <p className="text-center text-sm text-muted-foreground">
          Sem telemetria recebida ainda para esta embarcação.
        </p>
      )}
    </div>
  );
}

function buildHealth(position: Position | undefined, blocked: boolean) {
  const a = position?.attributes;
  const t = readTelemetry(position);

  // Energia
  const energia: HealthRow[] = [];
  if (t.batteryLevel !== undefined || t.battery !== undefined || t.power !== undefined) {
    const parts: string[] = [];
    if (t.batteryLevel !== undefined) parts.push(`${Math.round(t.batteryLevel)}%`);
    const volts = t.battery ?? t.power;
    if (volts !== undefined) parts.push(`${volts.toFixed(1).replace('.', ',')} V`);
    const low = t.batteryLevel !== undefined && t.batteryLevel <= 20;
    energia.push({
      icon: t.charging ? BatteryCharging : BatteryFull,
      label: t.charging ? 'Bateria (carregando)' : 'Bateria',
      value: parts.join(' · ') || '—',
      tone: low ? 'danger' : 'ok',
    });
  } else {
    energia.push({ icon: BatteryFull, label: 'Bateria', value: 'Sem dado', tone: 'muted' });
  }
  // Alimentação externa (conectada/desconectada → roda em bateria backup)
  if (t.power !== undefined || t.charging !== undefined) {
    const extOn = (t.power !== undefined && t.power > 6) || t.charging === true;
    energia.push({
      icon: extOn ? Plug : Unplug,
      label: 'Alimentação externa',
      value: extOn
        ? t.power !== undefined ? `Conectada · ${t.power.toFixed(1).replace('.', ',')} V` : 'Conectada'
        : 'Desconectada (bateria backup)',
      tone: extOn ? 'ok' : 'danger',
    });
  }

  // Casco / Porão
  const casco: HealthRow[] = [];
  const water = asBool(pick(a, ['water', 'waterDetected', 'bilgeWater', 'flood', 'waterAlarm', 'leak', 'bilge']));
  casco.push(
    water === undefined
      ? { icon: Droplets, label: 'Sensor de água', value: 'Sem dado', tone: 'muted' }
      : water
        ? { icon: Droplets, label: 'Sensor de água', value: 'ÁGUA DETECTADA', tone: 'danger' }
        : { icon: Droplets, label: 'Sensor de água', value: 'Seco', tone: 'ok' },
  );
  const pump = asBool(pick(a, ['pump', 'bilgePump', 'pumpStatus']));
  casco.push(
    pump === undefined
      ? { icon: Cog, label: 'Bomba de porão', value: 'Sem dado', tone: 'muted' }
      : { icon: Cog, label: 'Bomba de porão', value: pump ? 'Ligada' : 'Desligada', tone: pump ? 'warn' : 'ok' },
  );
  const temp = asNumber(pick(a, ['temperature', 'temp', 'deviceTemp', 'engineTemp', 'coolantTemp']));
  if (temp !== undefined) {
    casco.push({ icon: Thermometer, label: 'Temperatura', value: `${Math.round(temp)} °C`, tone: temp >= 95 ? 'danger' : 'ok' });
  }

  // Motor
  const motor: HealthRow[] = [];
  motor.push(
    t.ignition === undefined
      ? { icon: KeyRound, label: 'Ignição', value: 'Sem dado', tone: 'muted' }
      : { icon: KeyRound, label: 'Ignição', value: t.ignition ? 'Ligada' : 'Desligada', tone: 'ok' },
  );
  motor.push({
    icon: Gauge,
    label: 'Motor (bloqueio)',
    value: blocked ? 'Bloqueado' : 'Liberado',
    tone: blocked ? 'danger' : 'ok',
  });
  const hours = t.hours;
  if (hours !== undefined) {
    motor.push({ icon: Clock, label: 'Horas de motor', value: `${Math.round(hours / 3_600_000)} h`, tone: 'ok' });
  }
  const rpm = asNumber(pick(a, ['rpm']));
  if (rpm !== undefined) {
    motor.push({ icon: Waves, label: 'RPM', value: String(Math.round(rpm)), tone: 'ok' });
  }

  const overall: RowTone = water === true || blocked
    ? 'danger'
    : (t.batteryLevel !== undefined && t.batteryLevel <= 20) || pump === true
      ? 'warn'
      : 'ok';

  return { energia, casco, motor, overall };
}

function HealthSection({ title, rows }: { title: string; rows: HealthRow[] }) {
  return (
    <section className="space-y-2">
      <h2 className="px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</h2>
      <Card>
        <CardContent className="p-0">
          <ul className="divide-y divide-border">
            {rows.map((r, i) => (
              <li key={i} className="flex items-center gap-3 px-4 py-3">
                <span className={cn('flex h-8 w-8 items-center justify-center rounded-lg bg-secondary', TONE[r.tone])}>
                  <r.icon className="h-4 w-4" />
                </span>
                <span className="flex-1 text-sm font-medium">{r.label}</span>
                <span className={cn('text-sm font-semibold', TONE[r.tone])}>{r.value}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </section>
  );
}

function BackLink({ title }: { title?: string }) {
  return (
    <div className="flex items-center justify-between">
      <Link
        to={title ? '#' : '/devices'}
        onClick={(e) => {
          if (title) {
            e.preventDefault();
            window.history.back();
          }
        }}
        className="-ml-2 inline-flex items-center gap-0.5 rounded-md px-2 py-1 text-sm font-medium text-primary hover:bg-primary/5"
      >
        <ChevronLeft className="h-5 w-5" />
        Voltar
      </Link>
      <span className="truncate text-sm font-semibold text-foreground/70">
        {title ? `Saúde · ${title}` : 'Saúde'}
      </span>
      <span className="w-16" />
    </div>
  );
}
